/**
 * PackageHeader
 * Main header for correspondence package workspace.
 * Typographical, minimalist design with hover tooltips.
 */

import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';
import { Loader2 } from 'lucide-react';
import React from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { CorrespondenceChannel } from '@/services/api/correspondenceService';

import type {
  CorrespondencePackageSchema,
  CorrespondencePackageType,
  PackageValidationStatus,
  RenderStatus,
  TransmissionStatus,
} from '../types';
import { PackageTitle } from './PackageTitle';
import { StatusIndicator } from './StatusIndicator';
import { ValidationBanner } from './ValidationBanner';

// ============================================================================
// Types
// ============================================================================

interface PackageHeaderProps {
  /** Package data */
  packageData: CorrespondencePackageSchema;
  /** Validation status from context */
  validationStatus: PackageValidationStatus;
  /** Whether validation is loading */
  isLoadingValidation: boolean;
  /** Selected channel from transmission state */
  selectedChannel: CorrespondenceChannel | null;
  /** Recipient display name */
  recipientName: string | null | undefined;
  /** Related carrier name (from coverage when no recipient selected) */
  relatedCarrierName?: string | null;
  /** Whether package is currently rendering */
  isRendering: boolean;
  /** Whether render action is available */
  canRender: boolean;
  /** Whether any components have stale inputs and need re-rendering */
  hasStaleComponents?: boolean;
  /** Exit callback */
  onExit: () => void;
  /** Render callback */
  onRender: () => void;
  /** Exit icon style */
  exitIcon?: 'back' | 'close';
  /** Callback when transmission action is triggered from validation banner */
  onTransmissionAction?: (
    action: 'selectRecipient' | 'setTransmission',
  ) => void;
  /** Callback when client info action is triggered from validation banner */
  onClientInfoAction?: () => void;
  /** Current transmission state for validation banner CTAs */
  transmission?: Parameters<typeof ValidationBanner>[0]['transmission'];
  className?: string;
}

// ============================================================================
// Header Action Button
// ============================================================================

interface ActionButtonProps {
  icon: React.ReactNode;
  tooltip: string;
  onClick?: () => void;
  href?: string;
  disabled?: boolean;
  loading?: boolean;
}

const ActionButton: React.FC<ActionButtonProps> = ({
  icon,
  tooltip,
  onClick,
  href,
  disabled,
  loading,
}) => {
  const buttonClasses = cn(
    'flex items-center justify-center h-8 w-8 rounded-full transition-all',
    disabled
      ? 'text-zinc-300 cursor-not-allowed'
      : 'text-zinc-500 hover:text-zinc-900 hover:bg-white hover:shadow-sm border border-transparent hover:border-zinc-100',
  );

  const content = loading ? (
    <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
  ) : (
    icon
  );

  if (href && !disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={buttonClasses}
          >
            {content}
          </a>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs font-medium">
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={buttonClasses}
        >
          {content}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs font-medium">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const PackageHeader: React.FC<PackageHeaderProps> = ({
  packageData,
  validationStatus,
  isLoadingValidation,
  selectedChannel,
  recipientName,
  relatedCarrierName,
  isRendering,
  canRender,
  hasStaleComponents = false,
  onExit,
  onRender,
  exitIcon = 'close',
  onTransmissionAction,
  onClientInfoAction,
  transmission,
  className,
}) => {
  const { hasMissingFields } = validationStatus;
  const isTransmitted = packageData.transmitted_at != null;
  const messageStatus = packageData.message?.transmission_status ?? null;

  const showValidation =
    !isTransmitted &&
    (hasMissingFields || isLoadingValidation || !recipientName);

  return (
    <div className={cn('shrink-0 px-3 pt-3 pb-1 z-20', className)}>
      <div className="flex items-center justify-between bg-white border border-zinc-200 rounded-xl px-3 py-2">
        {/* Left: Exit + Title */}
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={onExit}
            className="flex items-center justify-center h-7 w-7 rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-600 transition-all flex-shrink-0"
          >
            {exitIcon === 'back' ? (
              <ArrowLeftIcon className="h-3.5 w-3.5" />
            ) : (
              <XMarkIcon className="h-3.5 w-3.5" />
            )}
          </button>

          <PackageTitle
            packageType={packageData.package_type as CorrespondencePackageType}
            channel={selectedChannel}
            recipientName={
              recipientName || packageData.recipient_display_name || null
            }
            relatedCarrierName={relatedCarrierName}
            date={isTransmitted ? packageData.transmitted_at : null}
          />
        </div>

        {/* Right: Validation + Status + Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Inline validation indicator */}
          {showValidation && (
            <ValidationBanner
              validationStatus={validationStatus}
              components={packageData.components}
              isLoading={isLoadingValidation}
              transmission={transmission}
              recipientName={recipientName}
              onTransmissionAction={onTransmissionAction}
              onClientInfoAction={onClientInfoAction}
            />
          )}

          <StatusIndicator
            renderStatus={packageData.status as RenderStatus}
            transmissionStatus={messageStatus as TransmissionStatus | null}
            transmitted={isTransmitted}
          />

          <div className="h-4 w-px bg-zinc-200" />

          <div className="flex items-center gap-0.5">
            {packageData.rendered_pdf_url && (
              <ActionButton
                icon={<ArrowDownTrayIcon className="h-3.5 w-3.5" />}
                tooltip="Download PDF"
                href={packageData.rendered_pdf_url}
              />
            )}

            {!isTransmitted && (
              <div className="relative">
                <ActionButton
                  icon={<ArrowPathIcon className="h-3.5 w-3.5" />}
                  tooltip={
                    isRendering
                      ? 'Generating...'
                      : hasStaleComponents
                        ? 'Re-render needed - inputs changed'
                        : 'Regenerate Preview'
                  }
                  onClick={onRender}
                  disabled={!canRender}
                  loading={isRendering}
                />
                {hasStaleComponents && !isRendering && (
                  <span className="absolute -top-0.5 -right-0.5 h-2 w-2 bg-amber-500 rounded-full" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
